import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { Integration } from '../integrations/types';
import useFullStoryIntegration, {
  type FullStoryProps,
} from '../integrations/useFullStoryIntegration';
import useGoogleAnalyticsIntegration from '../integrations/useGoogleAnalyticsIntegration';
import useIntercomIntegration from '../integrations/useIntercomIntegration';
import useMixpanelIntegration from '../integrations/useMixpanelIntegration';
import usePostHogIntegration from '../integrations/usePostHogIntegration';
import AnalyticsIntegrationsContext from './context';
import { simpleArrayCompare } from './utils';

export interface AnalyticsIntegrationsProviderProps {
  customIntegrations: Integration[];
  fullStory: FullStoryProps;
}

const AnalyticsIntegrationsProvider: React.FC<
  React.PropsWithChildren<AnalyticsIntegrationsProviderProps>
> = ({ children, customIntegrations, fullStory }) => {
  const intercomIntegration = useIntercomIntegration();
  const mixpanelIntegration = useMixpanelIntegration();
  const fullStoryIntegration = useFullStoryIntegration(fullStory);
  const googleAnalyticsIntegration = useGoogleAnalyticsIntegration();
  const postHogIntegration = usePostHogIntegration();

  const defaultIntegrationsRef = useRef([
    intercomIntegration,
    mixpanelIntegration,
    fullStoryIntegration,
    googleAnalyticsIntegration,
    postHogIntegration,
  ]);

  const [integrations, setIntegrations] = useState([
    ...defaultIntegrationsRef.current,
    ...customIntegrations,
  ]);

  const customIntegrationsRef = useRef(customIntegrations);
  useEffect(() => {
    const existingIntegrationNames = customIntegrationsRef.current.map((i) => i.name);
    const providedIntegrationNames = customIntegrations.map((i) => i.name);

    if (!simpleArrayCompare(existingIntegrationNames, providedIntegrationNames)) {
      customIntegrationsRef.current = customIntegrations;
      setIntegrations([...defaultIntegrationsRef.current, ...customIntegrations]);
    }
  }, [customIntegrations]);

  const contextValue = useMemo(
    () => ({
      integrations,
    }),
    [integrations],
  );

  return (
    <AnalyticsIntegrationsContext.Provider value={contextValue}>
      {children}
    </AnalyticsIntegrationsContext.Provider>
  );
};

export default AnalyticsIntegrationsProvider;
